'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type Step = 'step1' | 'step2';

/** 수정 페이지에서 상세 로드 후 스냅/상품 앱바 문구 분기 */
export type ShopEditCurationKind = 'goods' | 'snap';

interface ShopEditContextValue {
  step: Step;
  setStep: (step: Step) => void;
  curationKind: ShopEditCurationKind | null;
  setCurationKind: (kind: ShopEditCurationKind | null) => void;
}

const ShopEditContext = createContext<ShopEditContextValue | null>(null);

interface ShopEditProviderProps {
  children: ReactNode;
  /** 생성 플로우 등에서 경로만으로 goods/snap을 알 때(수정 페이지에서는 전달하지 않음) */
  initialCurationKind?: ShopEditCurationKind;
}

export const ShopEditProvider = ({ children, initialCurationKind }: ShopEditProviderProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [curationKind, setCurationKind] = useState<ShopEditCurationKind | null>(
    initialCurationKind ?? null,
  );

  const step: Step = searchParams.get('step') === '2' ? 'step2' : 'step1';

  const setStep = useCallback(
    (newStep: Step) => {
      if (newStep === step) return;

      if (newStep === 'step2') {
        router.push(`${pathname}?step=2`, { scroll: false });
        return;
      }

      router.back();
    },
    [step, pathname, router],
  );

  useEffect(() => {
    setCurationKind(initialCurationKind ?? null);
  }, [pathname, initialCurationKind]);

  const value = useMemo(
    () => ({
      step,
      setStep,
      curationKind,
      setCurationKind,
    }),
    [step, setStep, curationKind],
  );

  return <ShopEditContext.Provider value={value}>{children}</ShopEditContext.Provider>;
};

export const useShopEditContext = () => {
  const context = useContext(ShopEditContext);
  if (!context) {
    throw new Error('useShopEditContext must be used within a ShopEditProvider');
  }
  return context;
};
